import { Matrix4, Vector3, Quaternion, Box3 } from "three";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler";
import { AssetType, IRobotJoints } from "./enums";
import { GraphicService } from "./GraphicService";
import { ISceneConfiguration, ITransform } from "./interfaces";
import { Inventory } from "./InventoryService";
import { dataEvents, selectionEvents } from "./Events";
import { Robot } from "./Robot";
import { StaticMesh } from "./StaticMesh";
import { MathUtils } from "three";
import { moveBSU } from "./Interactions";

type BSUExportEntry = {
    transform: unknown; // or ITransform if you want stricter typing
    color: string;
    armHeight: number;
    pose: unknown;
    elbow: unknown;
    trocar?: {
        position: number[];
        orientation: number[];
    };
};

type SceneExportData = {
    name: string;
    roomSize: unknown;
    patientPosition: unknown;
    console?: unknown;
    stack?: unknown;
    trolley?: unknown;
    anesthesia?: unknown;
    surgeon?: unknown;
    slave?: unknown;
    iBSU: BSUExportEntry[];
    vBSU: BSUExportEntry[];
};

export class Room {
    currentConfiguration: ISceneConfiguration;

    SM_patient: StaticMesh | undefined;
    SM_patient_sampler: MeshSurfaceSampler | undefined;
    SM_bed: StaticMesh | undefined;
    SM_ibsu: { [id: string]: Robot } = {};
    index: { [id: string]: number } = {};
    SM_vbsu: { [id: string]: Robot } = {};
    SM_console: StaticMesh | undefined;
    SM_stack: StaticMesh | undefined;
    SM_trolley: StaticMesh | undefined;
    SM_anesthesia: StaticMesh | undefined;
    SM_trocar: { [id: string]: StaticMesh } = {};
    SM_surgeon: StaticMesh | undefined;
    SM_slave: StaticMesh | undefined;
    bsuPortLink: { [colorName: string]: { bsuName: string; portName: string } } = {};
    trocarToColor: { [id: string]: string } = {};
    procedureName = "";

    numberOfObjects = {
        console: 0,
        bsu: 0,
        stack: 0,
        trocar: 0,
        trolley: 0,
        surgeon: 0,
        slave: 0,
        anesthesia: 0,
    };
    isVBSU = false;

    selectedObject:
        | StaticMesh
        | Robot
        | { type: AssetType; SM_Patient: StaticMesh; SM_Bed: StaticMesh }
        | undefined;

    constructor(sceneConf: ISceneConfiguration) {
        this.currentConfiguration = sceneConf;

        selectionEvents.on("remove", () => {
            if (this.selectedObject instanceof Robot || this.selectedObject instanceof StaticMesh)
                this.remove(this.selectedObject);
        });
    }
    linkPortToBsu(colorName: string, portName: string): void {
        if (this.trocarToColor[portName]) {
            const oldColor = this.trocarToColor[portName];
            this.trocarToColor[portName] = "";
            if (this.bsuPortLink[oldColor]) {
                this.bsuPortLink[oldColor].portName = "";
                const bsuName = this.bsuPortLink[oldColor].bsuName;
                if (bsuName !== "") {
                    if (bsuName.startsWith(Inventory.iBsu.name)) {
                        this.SM_ibsu[bsuName].setPose("standard");
                    } else if (bsuName.startsWith(Inventory.vBsu.name)) {
                        this.SM_vbsu[bsuName].setPose("standard");
                    }
                }
            }
        }

        this.trocarToColor[portName] = colorName;
        if (this.bsuPortLink[colorName]) {
            const oldselectedPort = this.bsuPortLink[colorName].portName;
            if (this.trocarToColor[oldselectedPort] && this.trocarToColor[oldselectedPort] !== "") {
                this.trocarToColor[oldselectedPort] = "";
            }
            const smport = this.SM_trocar[portName];
            if (!smport) return;
            smport.meshes[0].material = Inventory.M_trocar;
            if (!this.SM_patient) return;
            if (!smport.isDepthValid(this.SM_patient)) return;
            this.bsuPortLink[colorName].portName = portName;
            moveBSU(colorName);
        } else {
            this.bsuPortLink[colorName] = { bsuName: "", portName: portName };
        }
    }
    getRotation(pos: Vector3): number {
        const rotation = 0;
        if (this.SM_bed) {
            const box = new Box3().setFromObject(this.SM_bed.group);

            const minimum = box.min;
            const maximum = box.max;
            if (pos.x > maximum.x) {
                if (pos.z < maximum.z) {
                } else if (pos.z < minimum.z) {
                }
            }
        }
        return rotation;
    }
    buildScene(): void {
        //this.clearAll();
        this.loadPatientPosition(this.currentConfiguration.patientPosition);
        // const ports = Inventory.Ports[this.currentConfiguration.patientPosition.name];
        const elbows: { [id: string]: number[] } = {};
        if (this.currentConfiguration.iBSU) {
            if (this.currentConfiguration.iBSU.length > 0) {
                this.currentConfiguration.iBSU.forEach((bsu) => {
                    const clonedBSU = this.addBSU("ibsu");

                    if (clonedBSU) {
                        if (bsu.color) {
                            const color = Inventory.getBSUColor(bsu.color);
                            if (color) {
                                clonedBSU.setLight(color);
                                if (bsu.elbow) elbows[bsu.color] = bsu.elbow;
                            }
                        }
                        clonedBSU.setPose("standard");
                        const pos = new Vector3().fromArray(bsu.transform.position);
                        const theta = MathUtils.degToRad(bsu.transform.rotation);
                        if (bsu.armHeight) clonedBSU.setArmHeight(bsu.armHeight);
                        clonedBSU.setRootRotation(theta, undefined);
                        clonedBSU.setRootPosition(pos, undefined);
                        if (bsu.trocar) {
                            const clonedPort = this.clonePort();
                            const bsuTarget = new Vector3().fromArray(bsu.trocar.position);
                            const bsuTargetOr = new Vector3().fromArray(bsu.trocar.orientation);

                            this.addPort(clonedPort, clonedPort.name);
                            clonedPort.setPosition(bsuTarget, undefined, false);
                            clonedPort.setOrientation(bsuTargetOr);
                            clonedPort.meshes[0].material = Inventory.M_trocar;
                            this.trocarToColor[clonedPort.name] = clonedBSU.lightColorName;
                            this.bsuPortLink[clonedBSU.lightColorName].portName = clonedPort.name;
                        }
                        if (bsu.pose) {
                            clonedBSU.resetPose(bsu.pose);
                            clonedBSU.cameraInstrumentVisibility(true);
                        }
                    }
                });
            }
        }
        if (this.currentConfiguration.vBSU) {
            if (this.currentConfiguration.vBSU.length > 0) {
                this.currentConfiguration.vBSU.forEach((bsu) => {
                    const clonedBSU = this.addBSU("vbsu");
                    if (clonedBSU) {
                        /* if (bsu.color) {
                            const color = Inventory.getBSUColor(bsu.color);
                            if (color) {
                                clonedBSU.setLight(color);
                                if (bsu.elbow) elbows[bsu.color] = bsu.elbow;
                            }
                        } */
                        const pos = new Vector3().fromArray(bsu.transform.position);
                        const theta = MathUtils.degToRad(bsu.transform.rotation);
                        clonedBSU.setPose("standard");
                        if (bsu.armHeight != undefined) clonedBSU.setArmHeight(bsu.armHeight);
                        else clonedBSU.setArmHeight(0.0);
                        clonedBSU.setRootRotation(theta, undefined);
                        clonedBSU.setRootPosition(pos, undefined);
                        // if (bsu.elbow) clonedBSU.moveElbow(new Vector3().fromArray(bsu.elbow));
                        if (bsu.trocar) {
                            const clonedPort = this.clonePort();
                            const bsuTarget = new Vector3().fromArray(bsu.trocar.position);
                            const bsuTargetOr = new Vector3().fromArray(bsu.trocar.orientation);

                            this.addPort(clonedPort, clonedPort.name);
                            clonedPort.setPosition(bsuTarget, undefined, false);
                            clonedPort.setOrientation(bsuTargetOr);
                            clonedPort.meshes[0].material = Inventory.M_trocar;
                            this.trocarToColor[clonedPort.name] = clonedBSU.lightColorName;
                            this.bsuPortLink[clonedBSU.lightColorName].portName = clonedPort.name;
                        }
                        if (bsu.pose) {
                            clonedBSU.resetPose(bsu.pose);
                            clonedBSU.cameraInstrumentVisibility(true);
                        }
                    }
                });
            }
        }

        if (this.currentConfiguration.console) {
            if (this.addConsole()) {
                const pos = new Vector3().fromArray(this.currentConfiguration.console.position);
                const theta = MathUtils.degToRad(this.currentConfiguration.console.rotation);
                this.SM_console?.setPosition(pos, undefined);
                this.SM_console?.setRotation(theta, undefined);
            }
        }
        if (this.currentConfiguration.stack) {
            if (this.addStack()) {
                const pos = new Vector3().fromArray(this.currentConfiguration.stack.position);
                const theta = MathUtils.degToRad(this.currentConfiguration.stack.rotation);
                this.SM_stack?.setPosition(pos, undefined);
                this.SM_stack?.setRotation(theta, undefined);
            }
        }

        if (this.currentConfiguration.trolley) {
            if (this.addTrolley()) {
                const pos = new Vector3().fromArray(this.currentConfiguration.trolley.position);
                const theta = MathUtils.degToRad(this.currentConfiguration.trolley.rotation);
                this.SM_trolley?.setPosition(pos, undefined);
                this.SM_trolley?.setRotation(theta, undefined);
            }
        }

        if (this.currentConfiguration.anesthesia) {
            if (this.addAnesthesia()) {
                const pos = new Vector3().fromArray(this.currentConfiguration.anesthesia.position);
                const theta = MathUtils.degToRad(this.currentConfiguration.anesthesia.rotation);
                this.SM_anesthesia?.setPosition(pos, undefined);
                this.SM_anesthesia?.setRotation(theta, undefined);
            }
        }

        if (this.currentConfiguration.surgeon) {
            if (this.addSurgeon()) {
                const pos = new Vector3().fromArray(this.currentConfiguration.surgeon.position);
                const theta = MathUtils.degToRad(this.currentConfiguration.surgeon.rotation);
                this.SM_surgeon?.setPosition(pos, undefined);
                this.SM_surgeon?.setRotation(theta, undefined);
            }
        }

        if (this.currentConfiguration.slave) {
            if (this.addSlave()) {
                const pos = new Vector3().fromArray(this.currentConfiguration.slave.position);
                const theta = MathUtils.degToRad(this.currentConfiguration.slave.rotation);
                this.SM_slave?.setPosition(pos, undefined);
                this.SM_slave?.setRotation(theta, undefined);
            }
        }

        this.updateCastables();
        GraphicService.clearHighlight();
        dataEvents.emit("dataLoaded");
    }

    reachAllPort(elbows: { [id: string]: number[] }): void {
        for (const colorID in this.bsuPortLink) {
            if (this.bsuPortLink[colorID].bsuName !== "") {
                const bsuName = this.bsuPortLink[colorID].bsuName;
                if (this.bsuPortLink[colorID].portName !== "") {
                    const portTransform = this.getPortTransform(colorID);
                    if (portTransform) {
                        if (bsuName.startsWith(Inventory.iBsu.name)) {
                            this.SM_ibsu[bsuName].setPose("standard");
                            this.SM_ibsu[bsuName].IKSolve(
                                portTransform.target,
                                portTransform.targetOrientation
                            );
                            if (elbows[colorID]) {
                                const elbowPos = new Vector3().fromArray(elbows[colorID]);
                                this.SM_ibsu[bsuName].moveElbow(elbowPos);
                            }
                        } else if (bsuName.startsWith(Inventory.vBsu.name)) {
                            if (this.SM_vbsu[bsuName]) {
                                this.SM_vbsu[bsuName].setPose("standard");
                                this.SM_vbsu[bsuName].IKSolve(
                                    portTransform.target,
                                    portTransform.targetOrientation
                                );
                                if (elbows[colorID]) {
                                    const elbowPos = new Vector3().fromArray(elbows[colorID]);
                                    this.SM_vbsu[bsuName].moveElbow(elbowPos);
                                }
                            } else {
                                this.remove(this.SM_trocar[this.bsuPortLink[colorID].portName]);
                            }
                        }
                    }
                } else {
                    if (this.SM_ibsu[bsuName] != undefined)
                        this.SM_ibsu[bsuName].setPose("standard");
                }
            } else {
                if (this.bsuPortLink[colorID].portName !== "") {
                    this.remove(this.SM_trocar[this.bsuPortLink[colorID].portName]);
                }
            }
        }
    }
    getPortTransform(id: string):
        | {
              target: Vector3;
              targetOrientation: Vector3;
          }
        | undefined {
        if (!this.bsuPortLink[id]) return;
        if (!this.bsuPortLink[id].portName) return;
        const portID = this.bsuPortLink[id].portName;
        if (!this.SM_trocar[portID]) return;
        this.SM_trocar[portID].group.updateMatrixWorld(true);
        const portMatrix = this.SM_trocar[portID].meshes[0].matrixWorld.clone();
        const target = new Vector3();
        target.setFromMatrixPosition(portMatrix.clone());
        const targetOr = this.getOrientation(portMatrix);
        return { target: target, targetOrientation: targetOr };
    }
    private getOrientation(m: Matrix4): Vector3 {
        const q = new Quaternion().setFromRotationMatrix(m);
        const targetOr = new Vector3(0.0, -1.0, 0.0);
        targetOr.applyQuaternion(q);
        targetOr.normalize();
        return targetOr;
    }
    setSceneConf(sceneConf: ISceneConfiguration): void {
        if (this.currentConfiguration) {
            this.currentConfiguration = sceneConf;
        }
    }
    setPatientPosition(procedure: { name: string; transform: ITransform }): void {
        this.procedureName = procedure.name;
        this.clearPorts();
        this.clearAllBSUs();
        //this.resetBSUPose();
        this.loadPatientPosition(procedure);
    }

    resetBSUPose(): void {
        for (const key in this.SM_ibsu) {
            this.SM_ibsu[key].setPose("standard");
        }
        for (const key in this.SM_vbsu) {
            this.SM_vbsu[key].setPose("standard");
        }
    }
    private loadPatientPosition(procedure: { name: string; transform: ITransform }): void {
        const patientPosition = procedure.name;
        this.SM_patient = Inventory.SM_PatientBed[patientPosition].SM_Patient;
        this.SM_bed = Inventory.SM_PatientBed[patientPosition].SM_Bed;
        const zone = this.SM_patient.group.userData["zone"];
        const dropZone = this.SM_patient.group.userData["dropzone"];
        GraphicService.scene.add(this.SM_patient.group);
        GraphicService.scene.add(this.SM_bed.group);
        const pos = new Vector3().fromArray(procedure.transform.position);
        const rot = MathUtils.degToRad(procedure.transform.rotation);
        this.SM_patient.setTransform({ pos, rot });
        this.SM_bed.setTransform({ pos, rot });
        this.SM_patient.group.updateMatrixWorld(true);
        const meshSample = dropZone ? dropZone : zone ? zone : this.SM_patient.meshes[0];
        this.SM_patient_sampler = new MeshSurfaceSampler(meshSample).build();
    }

    highlightPorts(): void {
        GraphicService.clearHighlight();
        for (const key in this.SM_trocar) {
            GraphicService.addToHighlight(this.SM_trocar[key]);
            GraphicService.control.target.copy(this.SM_trocar[key].group.position);
        }
    }

    buildPorts(
        ports: { [id: string]: { position: Vector3; matrix: Matrix4 } },
        offset: ITransform
    ): void {
        GraphicService.clearHighlight();
        for (const key in ports) {
            const m = ports[key].matrix.clone();
            //m.multiply(new Matrix4().makeRotationX(1.57));
            const clonePort = this.clonePort(m);
            clonePort.meshes[0].material = Inventory.M_trocar;
            const posOffset = new Vector3().fromArray(offset.position);
            this.addPort(clonePort, clonePort.name);
            /* this.trocarToColor[clonePort.name] = key;
            this.bsuPortLink[key].portName = clonePort.name; */

            this.linkPortToBsu(key, clonePort.name);

            //GraphicService.addToHighlight(clonePort);
            clonePort.translate(posOffset, undefined);
        }
    }
    addPort(port: StaticMesh, id: string): void {
        if (this.numberOfObjects.trocar < Inventory.MaxTrocar) {
            GraphicService.scene.add(port.group);
            port.meshes[0].visible = true;
            this.SM_trocar[id] = port;
            this.numberOfObjects.trocar++;
        }
    }
    updatePatientPosition(patientPosition: string): void {
        this.clearPatientBed();

        this.setPatientPosition({
            name: patientPosition,
            transform: this.currentConfiguration.patientPosition.transform,
        });

        this.updateCastables();
    }
    clearPatientBed(): void {
        if (this.SM_patient) {
            GraphicService.scene.remove(this.SM_patient.group);
            GraphicService.scene.remove(this.SM_patient.group.userData["zone"]);
            GraphicService.scene.remove(this.SM_patient.group.userData["dropzone"]);
            this.SM_patient = undefined;
            this.SM_patient_sampler = undefined;
        }

        if (this.SM_bed) {
            GraphicService.scene.remove(this.SM_bed.group);
            this.SM_bed = undefined;
        }
    }
    cloneBSU(r: Robot): Robot {
        const clonedBSU = r.clone();
        return clonedBSU;
    }
    addBSU(type: string): Robot | undefined {
        let clonedBSU: Robot | undefined;

        switch (type) {
            case "ibsu":
                if (this.numberOfObjects.bsu >= Inventory.MaxBSU) return;
                clonedBSU = this.cloneBSU(Inventory.iBsu);
                if (clonedBSU) {
                    this.SM_ibsu[clonedBSU.name] = clonedBSU;
                    this.numberOfObjects.bsu++;
                }
                break;
            case "vbsu":
                if (this.isVBSU) return;
                clonedBSU = Inventory.vBsu;
                this.isVBSU = true;
                this.SM_vbsu[clonedBSU.name] = Inventory.vBsu;
                Inventory.colors["white"].isUsed = false;
                clonedBSU.setLight(Inventory.colors["white"]);
                break;
        }

        if (!clonedBSU) return;
        GraphicService.scene.add(clonedBSU.group);
        GraphicService.scene.add(clonedBSU.collisiongroup);
        clonedBSU.setPose("standard");
        return clonedBSU;
    }
    clearPorts(): void {
        for (const key in this.SM_trocar) {
            const port = this.SM_trocar[key];
            delete this.SM_trocar[key];
            this.remove(port);
        }
        this.SM_trocar = {};
    }
    addConsole(): StaticMesh | undefined {
        if (this.numberOfObjects.console >= Inventory.MaxConsole) return;
        this.SM_console = Inventory.SM_console;
        GraphicService.scene.add(this.SM_console.group);
        this.numberOfObjects.console++;
        return this.SM_console;
    }
    addStack(): StaticMesh | undefined {
        if (this.numberOfObjects.stack >= Inventory.MaxStack) return;
        this.SM_stack = Inventory.SM_stack;
        GraphicService.scene.add(this.SM_stack.group);
        this.numberOfObjects.stack++;
        return this.SM_stack;
    }
    addTrolley(): StaticMesh | undefined {
        if (this.numberOfObjects.trolley >= Inventory.MaxTrolley) return;
        this.SM_trolley = Inventory.SM_trolley;
        GraphicService.scene.add(this.SM_trolley.group);
        this.numberOfObjects.trolley++;
        return this.SM_trolley;
    }
    addAnesthesia(): StaticMesh | undefined {
        if (this.numberOfObjects.anesthesia >= Inventory.MaxAnesthesia) return;
        this.SM_anesthesia = Inventory.SM_anesthesia;
        GraphicService.scene.add(this.SM_anesthesia.group);
        this.numberOfObjects.anesthesia++;
        return this.SM_anesthesia;
    }
    addSurgeon(): StaticMesh | undefined {
        if (this.numberOfObjects.surgeon >= Inventory.MaxSurgeon) return;
        this.SM_surgeon = Inventory.SM_surgeon;
        GraphicService.scene.add(this.SM_surgeon.group);
        this.numberOfObjects.surgeon++;
        return this.SM_surgeon;
    }
    addSlave(): StaticMesh | undefined {
        if (this.numberOfObjects.slave >= Inventory.MaxSlave) return;
        this.SM_slave = Inventory.SM_slave;
        GraphicService.scene.add(this.SM_slave.group);
        this.numberOfObjects.slave++;
        return this.SM_slave;
    }
    clonePort(matrix: Matrix4 | undefined = undefined): StaticMesh {
        const clonedPort = Inventory.SM_trocar.clone();
        if (matrix) clonedPort.group.applyMatrix4(matrix);
        return clonedPort;
    }
    remove(obj: Robot | StaticMesh): void {
        if (obj instanceof Robot) {
            GraphicService.scene.remove(obj.group);
            GraphicService.scene.remove(obj.collisiongroup);

            if (obj.name.startsWith(Inventory.iBsu.name)) {
                if (!this.SM_ibsu[obj.name]) return;
                const colorName = this.SM_ibsu[obj.name].lightColorName;
                if (Inventory.colors[colorName]) Inventory.colors[colorName].isUsed = false;
                if (this.bsuPortLink[colorName]) this.bsuPortLink[colorName].bsuName = "";
                obj.dispose();
                this.numberOfObjects.bsu--;
                delete this.SM_ibsu[obj.name];
            } else if (obj.name.startsWith(Inventory.vBsu.name)) {
                if (!this.SM_vbsu[obj.name]) return;
                // Inventory.colors[this.SM_vbsu[obj.name].lightColorName].isUsed = false;
                delete this.SM_vbsu[obj.name];
                this.isVBSU = false;
            }
        } else if (obj instanceof StaticMesh) {
            GraphicService.scene.remove(obj.group);
            GraphicService.scene.remove(obj.collisiongroup);
            switch (obj.type) {
                case AssetType.Trocar:
                    const portName = obj.name;
                    const color = this.trocarToColor[portName];
                    if (this.bsuPortLink[color]) {
                        this.bsuPortLink[color].portName = "";
                        if (this.bsuPortLink[color].bsuName) {
                            const bsuName = this.bsuPortLink[color].bsuName;
                            if (bsuName.startsWith(Inventory.vBsu.name)) {
                                if (this.SM_vbsu[bsuName])
                                    this.SM_vbsu[bsuName].setPose("standard");
                            } else if (bsuName.startsWith(Inventory.iBsu.name)) {
                                this.SM_ibsu[bsuName].setPose("standard");
                            }
                        }
                    }
                    if (this.trocarToColor[portName]) delete this.trocarToColor[portName];
                    delete this.SM_trocar[portName];
                    obj.dispose();
                    this.numberOfObjects.trocar--;
                    break;
                case AssetType.Stack:
                    this.SM_stack = undefined;
                    this.numberOfObjects.stack--;
                    break;
                case AssetType.Trolley:
                    this.SM_trolley = undefined;
                    this.numberOfObjects.trolley--;
                    break;
                case AssetType.Anesthesia:
                    this.SM_anesthesia = undefined;
                    this.numberOfObjects.anesthesia--;
                    break;
                case AssetType.Surgeon:
                    this.SM_surgeon = undefined;
                    this.numberOfObjects.surgeon--;
                    break;
                case AssetType.Slave:
                    this.SM_slave = undefined;
                    this.numberOfObjects.slave--;
                    break;
                case AssetType.Console:
                    this.SM_console = undefined;
                    this.numberOfObjects.console--;
                    break;
            }
        }
        //this.updateSceneConfiguration();
        this.updateCastables();
    }
    clearAll(): void {
        this.clearPatientBed();

        if (this.SM_console) this.remove(this.SM_console);
        if (this.SM_stack) this.remove(this.SM_stack);
        if (this.SM_trolley) this.remove(this.SM_trolley);
        if (this.SM_anesthesia) this.remove(this.SM_anesthesia);
        if (this.SM_surgeon) this.remove(this.SM_surgeon);
        if (this.SM_slave) this.remove(this.SM_slave);
        this.clearAllBSUs();
        this.clearPorts();
    }

    clearAllBSUs(): void {
        for (const key in this.SM_ibsu) {
            this.remove(this.SM_ibsu[key]);
        }
        for (const key in this.SM_vbsu) {
            this.remove(this.SM_vbsu[key]);
        }
    }

    reset(sceneConf: ISceneConfiguration): void {
        this.clearAll();
        this.currentConfiguration = sceneConf;
        this.buildScene();
    }

    updateCastables(): void {
        GraphicService.castableMeshes = [];
        GraphicService.armMeshes = [];
        /* this.SM_patient?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        });
        this.SM_bed?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        }); */
        this.SM_stack?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        });
        this.SM_console?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        });
        this.SM_trolley?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        });
        this.SM_anesthesia?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        });
        this.SM_surgeon?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        });
        this.SM_slave?.meshes.forEach((mesh) => {
            GraphicService.castableMeshes.push(mesh);
        });

        for (const key in this.SM_ibsu) {
            GraphicService.castableMeshes.push(this.SM_ibsu[key].base);
            this.SM_ibsu[key].ArmDecomposition.forEach((sph) => {
                if (
                    sph.userData.joint === IRobotJoints.Elbow ||
                    sph.userData.joint === IRobotJoints.Wrist
                )
                    GraphicService.armMeshes.push(sph);
                else {
                    GraphicService.castableMeshes.push(sph);
                }
            });
        }
        for (const key in this.SM_vbsu) {
            GraphicService.castableMeshes.push(this.SM_vbsu[key].base);
            this.SM_vbsu[key].ArmDecomposition.forEach((sph) => {
                if (
                    sph.userData.joint === IRobotJoints.Elbow ||
                    sph.userData.joint === IRobotJoints.Wrist
                )
                    GraphicService.armMeshes.push(sph);
                else {
                    GraphicService.castableMeshes.push(sph);
                }
            });
        }
        for (const key in this.SM_trocar) {
            this.SM_trocar[key].meshes.forEach((mesh) => {
                GraphicService.castableMeshes.push(mesh);
            });
        }
    }

    buildExportConfiguration(sceneName?: string): SceneExportData {
        const iBSU: BSUExportEntry[] = [];
        for (const key in this.SM_ibsu) {
            const bsu = this.SM_ibsu[key];
            const colorName = bsu.lightColorName;
            const portName = this.bsuPortLink[colorName]?.portName;
            const trocar = portName ? this.SM_trocar[portName] : undefined;

            const entry: any = {
                transform: bsu.getTransform(),
                color: colorName,
                armHeight: bsu.getArmHeight(),
                pose: bsu.getCurrentPose(),
                elbow: bsu.getElbowPosition(),
            };

            if (trocar) {
                trocar.group.updateMatrixWorld(true);
                const position = new Vector3();
                trocar.meshes[0].getWorldPosition(position);
                const orientation = new Vector3(0, -1, 0);
                orientation.applyQuaternion(trocar.group.quaternion);
                entry.trocar = {
                    position: position.toArray(),
                    orientation: orientation.toArray(),
                };
            }

            iBSU.push(entry);
        }

        const vBSU: BSUExportEntry[] = [];
        for (const key in this.SM_vbsu) {
            const bsu = this.SM_vbsu[key];
            const colorName = bsu.lightColorName;
            const portName = this.bsuPortLink[colorName]?.portName;
            const trocar = portName ? this.SM_trocar[portName] : undefined;

            const entry: any = {
                transform: bsu.getTransform(),
                color: colorName,
                armHeight: bsu.getArmHeight(),
                pose: bsu.getCurrentPose(),
                elbow: bsu.getElbowPosition(),
            };

            if (trocar) {
                trocar.group.updateMatrixWorld(true);
                const position = new Vector3();
                trocar.meshes[0].getWorldPosition(position);
                const orientation = new Vector3(0, -1, 0);
                orientation.applyQuaternion(trocar.group.quaternion);
                entry.trocar = {
                    position: position.toArray(),
                    orientation: orientation.toArray(),
                };
            }

            vBSU.push(entry);
        }

        return {
            name: sceneName || this.currentConfiguration.name || "scene",
            roomSize: this.currentConfiguration.roomSize,
            patientPosition: this.SM_bed
                ? { name: this.SM_bed.name, transform: this.SM_bed.getTransform() }
                : this.currentConfiguration.patientPosition,
            console: this.SM_console?.getTransform(),
            stack: this.SM_stack?.getTransform(),
            trolley: this.SM_trolley?.getTransform(),
            surgeon: this.SM_surgeon?.getTransform(),
            slave: this.SM_slave?.getTransform(),
            iBSU,
            vBSU,
        };
    }

    updateSceneConfiguration(): void {
        if (this.SM_bed) {
            //this.currentConfiguration.name = this.SM_bed.name;
            this.currentConfiguration.patientPosition.name = this.SM_bed.name;
            this.currentConfiguration.patientPosition.transform = this.SM_bed.getTransform();
        }

        if (this.SM_console) {
            this.currentConfiguration.console = this.SM_console.getTransform();
        }

        if (this.SM_stack) {
            this.currentConfiguration.stack = this.SM_stack.getTransform();
        }

        if (this.SM_trolley) {
            this.currentConfiguration.trolley = this.SM_trolley.getTransform();
        }

        if (this.SM_anesthesia) {
            this.currentConfiguration.anesthesia = this.SM_anesthesia.getTransform();
        }

        if (this.SM_surgeon) {
            this.currentConfiguration.surgeon = this.SM_surgeon.getTransform();
        }

        if (this.SM_slave) {
            this.currentConfiguration.slave = this.SM_slave.getTransform();
        }

        this.currentConfiguration.iBSU = [];
        for (const key in this.SM_ibsu) {
            const bsu = this.SM_ibsu[key];
            const colorName = bsu.lightColorName;

            // Find the trocar linked to this BSU
            const portName = this.bsuPortLink[colorName]?.portName;
            const trocar = portName ? this.SM_trocar[portName] : undefined;

            const bsuEntry: any = {
                transform: bsu.getTransform(),
                color: colorName,
                armHeight: bsu.getArmHeight?.() ?? 0,
                pose: bsu.getCurrentPose?.() ?? [],
                elbow: bsu.getElbowPosition?.() ?? [],
            };

            if (trocar) {
                trocar.group.updateMatrixWorld(true);

                // Position — world position of the mesh
                const position = new Vector3();
                trocar.meshes[0].getWorldPosition(position);

                // Orientation — reverse of setOrientation: apply group quaternion to (0, -1, 0)
                const orientation = new Vector3(0, -1, 0);
                orientation.applyQuaternion(trocar.group.quaternion);

                bsuEntry.trocar = {
                    position: position.toArray(),
                    orientation: orientation.toArray(),
                };
            }

            this.currentConfiguration.iBSU.push(bsuEntry);
        }

        this.currentConfiguration.vBSU = [];
        for (const key in this.SM_vbsu) {
            const bsu = this.SM_vbsu[key];
            const colorName = bsu.lightColorName;
            const portName = this.bsuPortLink[colorName]?.portName;
            const trocar = portName ? this.SM_trocar[portName] : undefined;

            const bsuEntry: any = {
                transform: bsu.getTransform(),
                color: colorName,
                armHeight: bsu.getArmHeight?.() ?? 0,
                pose: bsu.getCurrentPose?.() ?? [],
                elbow: bsu.getElbowPosition?.() ?? [],
            };

            if (trocar) {
                trocar.group.updateMatrixWorld(true);
                bsuEntry.trocar = {
                    position: trocar.meshes[0].getWorldPosition(new Vector3()).toArray(),
                    orientation: trocar.meshes[0].getWorldDirection(new Vector3()).toArray(),
                };
            }

            this.currentConfiguration.vBSU.push(bsuEntry);
        }
    }
    /* updateSize(newSize:IRoomSize){
        this.currentConfiguration.roomSize = newSize;
        this.builder.updateRoomSize(newSize);
    } */
}
